import { useState, useEffect, useRef } from 'react'
import {
    Grid,
    FormControl,
    IconButton,
    Button,
    Box,
    Switch,
    Typography,
    Chip,
    Paper,
    Tooltip
} from '@mui/material'
import {
    InfoOutlined as InfoIcon,
    Autorenew as AutoRenewIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    AttachFile as PaperclipIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material'
import { validate, format } from 'rut.js'
import './CreateUser.css'
import { LoadingModal } from '../../modals'
import { imageToBase64, ROLES } from '../../config'
import { useSitesContext, useUsersContext, useStatus } from '../../context'

// Allowed roles for receiving email notifications (Admins & Executives)
const ALLOWED_NOTIFICATION_ROLES = [
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.SAP_EXECUTIVE,
    ROLES.SUPERVISOR,
    ROLES.CHIEF_MACHINERY,
    ROLES.SHIFT_MANAGER,
    ROLES.OPERATOR_MACHINERY_UNIVERSAL
]

const generateSecurePassword = () => {
    const uppers = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
    const lowers = 'abcdefghijkmnpqrstuvwxyz'
    const numbers = '23456789'
    const symbols = '!@#$%&*?-'

    const getRandomChar = (str) => str.charAt(Math.floor(Math.random() * str.length))

    const passwordChars = [
        getRandomChar(uppers),
        getRandomChar(uppers),
        getRandomChar(symbols),
        getRandomChar(numbers),
        getRandomChar(numbers),
        getRandomChar(lowers),
        getRandomChar(lowers),
        getRandomChar(lowers)
    ]

    for (let i = passwordChars.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]]
    }

    return passwordChars.join('')
}

const CreateUser = ({ typeDisplay, uData: userData, setUserData }) => {
    const { sites } = useSitesContext()
    const { roles, users } = useUsersContext()
    const { setStatus } = useStatus()

    const [verPassword, setVerPassword] = useState('password')
    const [verConfirmarPassword, setVerConfirmarPassword] = useState('password')

    // Local state
    const [rolesListSelection, setRolesListSelection] = useState([])
    const [passwordNoIguales, setPasswordNoIguales] = useState(false)
    const [open, setOpen] = useState(false)
    const [imageUrl, setImageUrl] = useState()
    const [displayRut, setDisplayRut] = useState('')

    const fileInputRef = useRef(null)

    const selectedRoleDbName = userData?.roleDbName || userData?.role?.dbName
    const hasValidEmail = userData?.email && userData.email !== 'x@x.xx' && !userData.email.includes('@placeholder.icv')
    const isNotificationAllowed = selectedRoleDbName ? ALLOWED_NOTIFICATION_ROLES.includes(selectedRoleDbName) : false

    // Validation state classes
    const [rutClass, setRutClass] = useState('inputClass')
    const [passwordClass, setPasswordClass] = useState('isInvalid')
    const [confirmPasswordClass, setConfirmPasswordClass] = useState('isInvalid')
    const [phoneClass, setPhoneClass] = useState('inputClass')

    useEffect(() => {
        if (roles && roles.length > 0) {
            const allRoles = roles
                .filter(role => role.dbName !== ROLES.SUPER_ADMIN)
                .map(role => ({ ...role }))

            if (userData && userData.role) {
                const roleIdToSelect = userData.role._id || userData.role
                allRoles.forEach(role => {
                    role.selected = role._id === roleIdToSelect
                })
            }
            setRolesListSelection(allRoles)
        }
    }, [roles, userData?.role])

    useEffect(() => {
        if (userData && userData.rut) {
            setDisplayRut(format(userData.rut))
        } else {
            setDisplayRut('')
        }
    }, [userData.rut])

    const cleanCurrentRut = userData?.rut ? userData.rut.replace(/\./g, '') : ''
    const duplicateUser = (typeDisplay === 'Nuevo usuario' && cleanCurrentRut && users)
        ? users.find(u => u.rut && u.rut.replace(/\./g, '') === cleanCurrentRut)
        : null

    useEffect(() => {
        if (userData.password && userData.confirmPassword) {
            if (userData.password.length >= 6 && userData.confirmPassword.length >= 6) {
                if (userData.password === userData.confirmPassword) {
                    setPasswordNoIguales(false)
                    setPasswordClass('isValid')
                    setConfirmPasswordClass('isValid')
                } else {
                    setPasswordNoIguales(true)
                    setPasswordClass('isInvalid')
                    setConfirmPasswordClass('isInvalid')
                }
            } else {
                setPasswordClass('isInvalid')
                setConfirmPasswordClass('isInvalid')
            }
        }
        if (userData.rut && !validate(userData.rut)) {
            setRutClass('isInvalid')
        } else if (duplicateUser) {
            setRutClass('isInvalid')
        } else if (userData.rut) {
            setRutClass('isValid')
        } else {
            setRutClass('inputClass')
        }
    }, [userData.password, userData.confirmPassword, userData.rut, duplicateUser])

    useEffect(() => {
        if (userData.phone && userData.phone.length === 9) {
            setPhoneClass('isValid')
        } else if (userData.phone && userData.phone.length > 0) {
            setPhoneClass('isInvalid')
        } else {
            setPhoneClass('inputClass')
        }
    }, [userData.phone])

    const cambiarVistaPassword = () => {
        setVerPassword(verPassword === 'password' ? 'text' : 'password')
    }

    const cambiarVistaConfirmarPassword = () => {
        setVerConfirmarPassword(verConfirmarPassword === 'password' ? 'text' : 'password')
    }

    const changeRut = (value) => {
        setDisplayRut(format(value))
        const cleanRut = format(value).replace(/\./g, '')
        setUserData({ ...userData, rut: cleanRut })
    }

    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword()
        setUserData({
            ...userData,
            password: newPassword,
            confirmPassword: newPassword
        })
        setVerPassword('text')
        setVerConfirmarPassword('text')
        // setStatus({
        //     status: 'completed',
        //     title: 'Contraseña generada',
        //     message: `Nueva contraseña segura: ${newPassword}`,
        //     severity: 'info'
        // })
    }

    const uploadImageProfile = async (file) => {
        const image = await imageToBase64(file)
        setImageUrl(image)
    }

    const openFile = () => {
        if (userData && userData._id) {
            if (fileInputRef.current) {
                fileInputRef.current.click()
            }
        } else {
            setStatus({
                status: 'error',
                title: 'Foto de perfil',
                message: 'Solo se puede agregar foto de perfil una vez creado el usuario.',
                severity: 'info'
            })
        }
    }

    const handleSiteSelection = (siteId) => {
        const currentObras = userData.obras || []
        let newObras = currentObras.map(obra => (typeof obra === 'object' && obra !== null ? obra._id : obra))

        if (newObras.includes(siteId)) {
            newObras = newObras.filter(id => id !== siteId)
        } else {
            if (newObras.length >= 3) {
                setStatus({
                    status: 'error',
                    title: 'Límite de Obras',
                    message: 'Solo se permite seleccionar un máximo de 3 obras por usuario.',
                    severity: 'warning'
                })
                return
            }
            newObras.push(siteId)
        }
        setUserData({ ...userData, obras: newObras })
    }

    const selectRoleByIndex = (index) => {
        const newRolesList = [...rolesListSelection]
        let selectedRoleObj = null

        newRolesList.forEach((role, i) => {
            role.selected = i === index ? !role.selected : false
            if (role.selected) {
                selectedRoleObj = role
            }
        })
        setRolesListSelection(newRolesList)

        if (selectedRoleObj) {
            const isAllowed = ALLOWED_NOTIFICATION_ROLES.includes(selectedRoleObj.dbName)
            setUserData({
                ...userData,
                role: selectedRoleObj,
                roleName: selectedRoleObj.name,
                roleDbName: selectedRoleObj.dbName,
                mailNotification: isAllowed ? userData.mailNotification : false
            })
        } else {
            setUserData({
                ...userData,
                role: null,
                roleName: null,
                roleDbName: null,
                mailNotification: false
            })
        }
    }

    const currentSelectedObrasIds = (userData.obras || []).map(obra => typeof obra === 'object' && obra !== null ? obra._id : obra)

    return (
        <div style={{ width: '100%', paddingBottom: 10 }}>
            <Grid container spacing={1.5}>
                {/* ================= COLUMNA 1: INFORMACIÓN DEL USUARIO (md=6) ================= */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#ffffff', height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#333', fontSize: 13 }}>
                            Información del Usuario *
                        </Typography>

                        {typeDisplay !== 'Nuevo usuario' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: 11 }}>Foto de perfil</Typography>
                                <button
                                    style={{ height: 120, width: 100, borderRadius: 10, border: '2px dashed #ccc', backgroundColor: '#fafafa', cursor: 'pointer' }}
                                    onClick={() => openFile()}
                                >
                                    {!imageUrl ? (
                                        <div style={{ color: '#666', fontSize: 10 }}>
                                            <PaperclipIcon style={{ fontSize: 16 }} />
                                            <br />CARGAR FOTO
                                        </div>
                                    ) : (
                                        <img src={imageUrl} alt="Profile" style={{ objectFit: 'cover', width: '100%', height: '100%', borderRadius: 8 }} />
                                    )}
                                </button>
                                <input ref={fileInputRef} autoComplete="off" type="file" id="profileImage" onChange={(e) => uploadImageProfile(e.target.files[0])} hidden />
                            </Box>
                        )}

                        <Grid container spacing={2}>
                            {/* Row 1: rut, correo */}
                            <Grid item xs={6}>
                                <FormControl style={{ width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, fontSize: 11 }}>RUT *</Typography>
                                    <input
                                        autoComplete="off"
                                        required
                                        id="rut"
                                        className={rutClass}
                                        type="text"
                                        minLength={11}
                                        maxLength={12}
                                        onInput={(e) => changeRut(e.target.value)}
                                        value={displayRut}
                                        placeholder="11.222.333-K"
                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 13, padding: '0 8px', border: '1px solid #ccc' }}
                                    />
                                    {duplicateUser && (
                                        <Typography variant="caption" sx={{ color: 'error.main', fontSize: 10, display: 'block', mt: 0.3, fontWeight: 'bold' }}>
                                            Ya registrado: {duplicateUser.name} {duplicateUser.lastName}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl style={{ width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, fontSize: 11 }}>Correo electrónico *</Typography>
                                    <input
                                        autoComplete="off"
                                        id="email"
                                        onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                                        value={userData.email || ''}
                                        placeholder="nombre@correo.cl"
                                        type="email"
                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 13, padding: '0 8px', border: '1px solid #ccc' }}
                                    />
                                </FormControl>
                            </Grid>

                            {/* Row 2: nombre, apellido */}
                            <Grid item xs={6}>
                                <FormControl style={{ width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, fontSize: 11 }}>Nombre *</Typography>
                                    <input
                                        autoComplete="off"
                                        required
                                        className="inputClass"
                                        id="name"
                                        onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                                        value={userData.name || ''}
                                        placeholder="John"
                                        type="text"
                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 13, padding: '0 8px', border: '1px solid #ccc' }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl style={{ width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, fontSize: 11 }}>Apellido *</Typography>
                                    <input
                                        autoComplete="off"
                                        required
                                        className="inputClass"
                                        id="lastName"
                                        onChange={(e) => setUserData({ ...userData, lastName: e.target.value })}
                                        value={userData.lastName || ''}
                                        placeholder="Doe"
                                        type="text"
                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 13, padding: '0 8px', border: '1px solid #ccc' }}
                                    />
                                </FormControl>
                            </Grid>

                            {/* Row 3: telefono, Notificaciones Switch */}
                            <Grid item xs={6}>
                                <FormControl style={{ width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, fontSize: 11 }}>Teléfono</Typography>
                                    <input
                                        autoComplete="off"
                                        className={phoneClass}
                                        id="phone"
                                        minLength={9}
                                        onInput={(e) => { e.target.value = e.target.value.slice(0, 9) }}
                                        onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
                                        value={userData.phone || ''}
                                        placeholder="999998888"
                                        type="text"
                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 13, padding: '0 8px', border: '1px solid #ccc' }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 0.6,
                                        borderRadius: 1.5,
                                        bgcolor: (isNotificationAllowed && hasValidEmail) ? '#f4f6f8' : '#fafafa',
                                        borderStyle: (isNotificationAllowed && hasValidEmail) ? 'solid' : 'dashed',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        mt: 1.5
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Switch
                                            size="small"
                                            checked={!!userData.mailNotification && isNotificationAllowed && hasValidEmail}
                                            onChange={(e) => setUserData({ ...userData, mailNotification: e.target.checked })}
                                            disabled={!isNotificationAllowed || !hasValidEmail}
                                        />
                                        <div>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    display: 'block',
                                                    fontSize: 10,
                                                    color: (isNotificationAllowed && hasValidEmail) ? 'text.primary' : 'text.disabled'
                                                }}
                                            >
                                                Notificaciones correo
                                            </Typography>
                                        </div>
                                    </div>
                                    {!isNotificationAllowed ? (
                                        <Typography variant="caption" sx={{ display: 'block', fontSize: 9, color: 'text.secondary', pl: 0.8, mt: -0.2 }}>
                                            Para habilitar debe seleccionar un jefe, supervisor, sap o admin.
                                        </Typography>
                                    ) : !hasValidEmail ? (
                                        <Typography variant="caption" sx={{ display: 'block', fontSize: 9, color: 'error.main', pl: 0.8, mt: -0.2, fontWeight: 'bold' }}>
                                            Requiere correo
                                        </Typography>
                                    ) : null}
                                </Paper>
                            </Grid>

                            {/* Row 4: contraseñas (solo creación) */}
                            {typeDisplay === 'Nuevo usuario' && (
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <FormControl style={{ width: '100%' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, display: 'block', fontSize: 11 }}>Contraseña *</Typography>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <input
                                                        autoComplete="off"
                                                        id="passw"
                                                        required
                                                        className={passwordClass}
                                                        onChange={(e) => setUserData({ ...userData, password: e.target.value })}
                                                        value={userData.password || ''}
                                                        placeholder="Mínimo 6"
                                                        type={verPassword}
                                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 12, padding: '0 8px', border: '1px solid #ccc' }}
                                                        className={passwordClass}
                                                    />
                                                    <IconButton size="small" onClick={cambiarVistaPassword} sx={{ ml: -3.8 }}>
                                                        {verPassword === 'password' ? <VisibilityIcon style={{ fontSize: 15 }} /> : <VisibilityOffIcon style={{ fontSize: 15 }} />}
                                                    </IconButton>
                                                </div>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <FormControl style={{ width: '100%' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.2, display: 'block', fontSize: 11 }}>Confirmar *</Typography>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <input
                                                        autoComplete="off"
                                                        id="c_passw"
                                                        required
                                                        className={confirmPasswordClass}
                                                        onChange={(e) => setUserData({ ...userData, confirmPassword: e.target.value })}
                                                        value={userData.confirmPassword || ''}
                                                        placeholder="Repita"
                                                        type={verConfirmarPassword}
                                                        style={{ width: '100%', height: 32, borderRadius: 6, fontSize: 12, padding: '0 8px', border: '1px solid #ccc' }}
                                                    />
                                                    <IconButton size="small" onClick={cambiarVistaConfirmarPassword} sx={{ ml: -3.8 }}>
                                                        {verConfirmarPassword === 'password' ? <VisibilityIcon style={{ fontSize: 15 }} /> : <VisibilityOffIcon style={{ fontSize: 15 }} />}
                                                    </IconButton>
                                                </div>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Button
                                                variant="outlined"
                                                color="success"
                                                size="small"
                                                onClick={handleGeneratePassword}
                                                startIcon={<AutoRenewIcon style={{ fontSize: 15 }} />}
                                                fullWidth
                                                sx={{
                                                    py: 0.4,
                                                    borderRadius: 1.5,
                                                    textTransform: 'none',
                                                    fontSize: 11,
                                                    fontWeight: 'bold',
                                                    borderColor: '#2e7d32',
                                                    color: '#2e7d32',
                                                    '&:hover': { bgcolor: '#e8f5e9', borderColor: '#1b5e20' }
                                                }}
                                            >
                                                Generar clave segura
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                        {/* ================= COLUMNA 3: OBRAS (SIEMPRE ABIERTO EN DIV) ================= */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fafafa', height: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', fontSize: 13 }}>
                                        Seleccion de Obras (Máx 3) *
                                    </Typography>
                                    <Chip
                                        label={`${currentSelectedObrasIds.length}/3`}
                                        color={currentSelectedObrasIds.length > 0 ? 'success' : 'default'}
                                        size="small"
                                        sx={{ fontWeight: 'bold', height: 20, fontSize: 10 }}
                                    />
                                </div>
                                {currentSelectedObrasIds.length >= 3 && (
                                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', fontSize: 10 }}>
                                        Maximo 3 Obras
                                    </Typography>
                                )}
                                <div style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 2 }}>
                                    <Grid container spacing={0.8}>
                                        {(sites || []).map((site) => {
                                            const isChecked = currentSelectedObrasIds.includes(site._id)
                                            const isDisabled = !isChecked && currentSelectedObrasIds.length >= 3
                                            return (
                                                <Grid item xs={12} key={site._id}>
                                                    <Button
                                                        variant={isChecked ? "contained" : "outlined"}
                                                        color={isChecked ? "success" : "inherit"}
                                                        onClick={() => handleSiteSelection(site._id)}
                                                        disabled={isDisabled}
                                                        fullWidth
                                                        sx={{
                                                            justifyContent: 'flex-start',
                                                            textTransform: 'none',
                                                            py: 0.5,
                                                            px: 1,
                                                            borderRadius: 1.5,
                                                            fontSize: 11,
                                                            fontWeight: isChecked ? 'bold' : 'normal',
                                                            borderColor: isChecked ? '#2e7d32' : '#e0e0e0',
                                                            bgcolor: isChecked ? '#e8f5e9' : '#ffffff',
                                                            color: isChecked ? '#1b5e20' : '#333',
                                                            '&:hover': { bgcolor: isChecked ? '#c8e6c9' : '#f5f5f5' }
                                                        }}
                                                    >
                                                        {isChecked && <CheckCircleIcon style={{ fontSize: 14, marginRight: 4, color: '#2e7d32' }} />}
                                                        <span style={{ textAlign: 'left', wordBreak: 'break-word' }}>{site.descripcion}</span>
                                                    </Button>
                                                </Grid>
                                            )
                                        })}
                                    </Grid>
                                </div>
                            </Paper>
                        </Grid>

                        {/* ================= COLUMNA 4: ROLES (SELECCIÓN POR BOTÓN + HOVER FLOTANTE) ================= */}
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#ffffff', height: '100%' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333', fontSize: 13 }}>
                                    4. Selección de Rol *
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.8, fontSize: 10 }}>
                                    Clic para seleccionar. Hover en (i) para ver permisos.
                                </Typography>
                                <div style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 2 }}>
                                    {rolesListSelection.map((role, n) => (
                                        <Box key={role._id || n} sx={{ display: 'flex', alignItems: 'center', mb: 0.8, gap: 0.5 }}>
                                            <Button
                                                variant={role.selected ? "contained" : "outlined"}
                                                color={role.selected ? "primary" : "inherit"}
                                                onClick={() => selectRoleByIndex(n)}
                                                fullWidth
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    textTransform: 'none',
                                                    py: 0.5,
                                                    px: 1,
                                                    borderRadius: 1.5,
                                                    fontSize: 11,
                                                    fontWeight: role.selected ? 'bold' : 'normal',
                                                    borderColor: role.selected ? '#1976d2' : '#e0e0e0',
                                                    bgcolor: role.selected ? '#e3f2fd' : '#ffffff',
                                                    color: role.selected ? '#1565c0' : '#333',
                                                    '&:hover': { bgcolor: role.selected ? '#bbdefb' : '#f5f5f5' }
                                                }}
                                            >
                                                {role.selected && <CheckCircleIcon style={{ fontSize: 14, marginRight: 4, color: '#1976d2' }} />}
                                                <span style={{ textAlign: 'left', flexGrow: 1 }}>{role.name}</span>
                                            </Button>

                                            <Tooltip
                                                arrow
                                                placement="left"
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: '#1e293b',
                                                            boxShadow: 4,
                                                            p: 1.2,
                                                            borderRadius: 2,
                                                            maxWidth: 260
                                                        }
                                                    }
                                                }}
                                                title={
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#60a5fa', mb: 0.5, fontSize: 12 }}>
                                                            {role.name}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mb: 0.8, color: '#cbd5e1', fontSize: 11 }}>
                                                            {role.description || 'Sin descripción registrada.'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: '#f8fafc', fontSize: 11 }}>
                                                            Permisos ({(role.permissions || []).length}):
                                                        </Typography>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxHeight: 120, overflowY: 'auto' }}>
                                                            {(role.permissions || []).length > 0 ? (
                                                                role.permissions.map((p, pIdx) => (
                                                                    <Chip
                                                                        key={p._id || pIdx}
                                                                        label={typeof p === 'object' ? (p.description || p.key) : p}
                                                                        size="small"
                                                                        sx={{ fontSize: 9, height: 18, bgcolor: 'rgba(255,255,255,0.15)', color: '#f1f5f9' }}
                                                                    />
                                                                ))
                                                            ) : (
                                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 10 }}>Sin permisos específicos</Typography>
                                                            )}
                                                        </div>
                                                    </Box>
                                                }
                                            >
                                                <IconButton size="small" sx={{ color: '#1976d2', p: 0.3 }}>
                                                    <InfoIcon style={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                </div>
                            </Paper>
                        </Grid>
            </Grid>

            {/* Password mismatch error banner */}
            {passwordNoIguales && (
                <Paper sx={{ mt: 1, p: 1, bgcolor: '#ffebee', borderLeft: '4px solid #d32f2f' }}>
                    <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                        Las contraseñas no coinciden. Por favor verifique los campos.
                    </Typography>
                </Paper>
            )}

            <LoadingModal open={open} withProgress={false} loadingData={'Espere...'} />
        </div>
    )
}

export default CreateUser
